import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, Check, RotateCcw, X } from 'lucide-react';
import { t } from '../lib/i18n';
import { useShoppingList } from '../context/ShoppingListContext';
import StorySlide from './StorySlide';
import SponsorSlide from './SponsorSlide';

const flatten = (rows, startBrandId) => {
  const k = Math.max(0, rows.findIndex((b) => b.brand_id === startBrandId));
  return [...rows.slice(k), ...rows.slice(0, k)].flatMap((b) => b.videos.map((v) => ({ ...v, brand: v.brand || b.brand, brand_id: v.brand_id || b.brand_id, slug: v.slug || b.slug, logo: b.logo })));
};

const StoryFeed = ({ rows, startBrandId, onClose, onBrandSeen }) => {
  const items = useMemo(() => flatten(rows, startBrandId), [rows, startBrandId]);
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const box = useRef(null);
  const list = useShoppingList();
  const total = items.length + 1;
  const goTo = useCallback((k) => { const el = box.current; if (!el) return; el.scrollTo({ top: Math.max(0, Math.min(total - 1, k)) * el.clientHeight, behavior: 'smooth' }); }, [total]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowDown' || e.key === 'ArrowRight') goTo(active + 1); if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') goTo(active - 1); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [onClose, goTo, active]);

  useEffect(() => {
    const el = box.current; if (!el) return;
    const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) setActive(Number(en.target.dataset.index)); }), { root: el, threshold: 0.6 });
    el.querySelectorAll('[data-index]').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [total]);

  useEffect(() => {
    const el = box.current; if (!el) return;
    let lock = 0;
    const onWheel = (e) => { e.preventDefault(); const now = Date.now(); if (now - lock < 650 || Math.abs(e.deltaY) < 15) return; lock = now; goTo(active + (e.deltaY > 0 ? 1 : -1)); };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [goTo, active]);

  useEffect(() => { const v = items[active]; if (v) onBrandSeen?.(v.brand_id); }, [active, items, onBrandSeen]);

  const ended = active >= items.length;
  return createPortal(
    <div className="fixed inset-0 z-[450] bg-black flex items-center justify-center select-none" data-testid="story-feed" dir="ltr" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative w-full h-full sm:w-[420px] sm:h-[min(92vh,880px)] sm:rounded-3xl overflow-hidden bg-[#0b0b0b] shadow-2xl">
        <div ref={box} className="h-full w-full overflow-y-auto snap-y snap-mandatory scrollbar-none overscroll-contain" data-testid="story-feed-scroller">
          {items.map((v, k) => v.kind === 'sponsor' ? <SponsorSlide key={v.id} v={v} index={k} active={k === active} onEnded={() => goTo(k + 1)} onClose={onClose} counter={`${k + 1} / ${items.length}`} /> : <StorySlide key={`${v.brand_id}-${v.id}`} v={v} index={k} active={k === active} near={Math.abs(k - active) <= 1} muted={muted} setMuted={setMuted} list={list} onEnded={() => goTo(k + 1)} onClose={onClose} counter={`${k + 1} / ${items.length}`} />)}
          <section data-index={items.length} className="relative h-full w-full snap-start snap-always flex flex-col items-center justify-center text-center text-white p-8 gap-4" data-testid="story-feed-end">
            <span className="w-20 h-20 rounded-full bg-[#E50914] flex items-center justify-center"><Check size={40} strokeWidth={3} /></span>
            <h2 className="text-2xl font-black">{t('Vous avez tout vu')}</h2>
            <p className="text-sm text-[#B3B3B3]">{t('Revenez bientôt pour de nouvelles stories')}</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => goTo(0)} className="h-11 px-5 rounded-full bg-white/15 hover:bg-white/25 font-bold text-sm inline-flex items-center gap-2" data-testid="story-feed-restart"><RotateCcw size={16} /> {t('Retour au début')}</button>
              <button onClick={onClose} className="h-11 px-6 rounded-full bg-[#E50914] font-bold text-sm inline-flex items-center gap-2" data-testid="story-feed-close"><X size={16} /> {t('Fermer')}</button>
            </div>
          </section>
        </div>
        {active === 0 && items.length > 1 && <p className="absolute top-1/2 -translate-y-1/2 inset-x-0 text-center text-[11px] text-white/70 pointer-events-none animate-bounce" data-testid="story-swipe-hint">{t('Glissez vers le haut pour la prochaine story')} ↑</p>}
        <div className="hidden sm:flex flex-col gap-2 absolute right-3 top-1/2 -translate-y-1/2">
          <button onClick={() => goTo(active - 1)} disabled={active === 0} className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white disabled:opacity-20 flex items-center justify-center" aria-label="Précédent" data-testid="story-prev"><ChevronUp size={20} /></button>
          <button onClick={() => goTo(active + 1)} disabled={ended} className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white disabled:opacity-20 flex items-center justify-center" aria-label="Suivant" data-testid="story-next"><ChevronDown size={20} /></button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default StoryFeed;
